import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { snagService } from '../../services/snagService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { pdfExportService } from '../../services/pdfExportService';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultDashboard } from '../../utils/roleAccess';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';

import SnagTable from './components/SnagTable';
import SnagCreateForm from './components/SnagCreateForm';
import SnagDeleteConfirmModal from './components/SnagDeleteConfirmModal';
import ProgressBar from '../../components/ui/ProgressBar';




const SnagsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const siteIdParam = searchParams?.get('siteId');
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  // Gate on the active filter site — when filter is empty/All Sites, no gate fires.
  const { status: filterSiteStatus, isReadOnly: filterSiteReadOnly } = useSiteReadOnly(siteIdParam || null);

  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(true);
  const [snags, setSnags] = useState([]);
  const [sites, setSites] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    siteId: siteIdParam || ''
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [requirePhotoEvidenceSnag, setRequirePhotoEvidenceSnag] = useState(false);

  const breadcrumbItems = [
    { label: t('home'), href: getDefaultDashboard(userProfile?.role) },
    { label: t('snags') }
  ];

  const canCreate = ['admin', 'site_manager', 'worker']?.includes(userProfile?.role);
  // NSherjFh PR 4: Delete + Restore + the 'View deleted' toggle are
  // admin-only. Site managers / workers retain create + view + status
  // updates.
  const isAdmin = userProfile?.role === 'admin';
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingSnag, setDeletingSnag] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, [viewDeleted]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const companyResult = await companyService?.getUserCompany();
      if (companyResult?.data) {
        setCompanyId(companyResult?.data?.id);
        setCompanyName(companyResult?.data?.company_name || companyResult?.data?.name || '');
        setRequirePhotoEvidenceSnag(companyResult?.data?.require_photo_evidence_snag ?? false);
      }
      // Scope sites to company (RLS ensures site_manager only sees their company's sites)
      const [snagsResult, sitesResult] = await Promise.all([
        snagService?.getAllSnags({ includeDeleted: viewDeleted }),
        siteService?.getSelectableSites(),
      ]);
      // NSherjFh PR 4: deleted-view shows ONLY soft-deleted snags. The
      // service returns BOTH active and deleted when includeDeleted is
      // true; narrow to the deleted ones for display.
      const allSnags = snagsResult?.data || [];
      setSnags(viewDeleted ? allSnags?.filter((s) => s?.deleted_at) : allSnags);
      if (sitesResult?.data) setSites(sitesResult?.data);
    } catch (err) {
      console.error('Error loading snags:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSnagCreated = (newSnag) => {
    setSnags(prev => [newSnag, ...prev]);
    setActiveTab('list');
  };

  const handleStatusChange = async (snagId, newStatus) => {
    try {
      await snagService?.updateSnagStatus(snagId, newStatus);
      setSnags(prev => prev?.map(snag =>
        snag?.id === snagId ? { ...snag, status: newStatus } : snag
      ));
    } catch (err) {
      console.error('Error updating snag status:', err);
    }
  };

  // NSherjFh PR 4 — soft-delete handlers. Mirrors PR 3's rams-
  // management flow: open modal → confirm → optimistic remove. Status
  // (open / in progress / resolved / closed) is preserved across the
  // soft-delete round trip; it's a separate axis from deleted_at.
  const handleDeleteClick = (snag) => {
    setDeletingSnag(snag);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingSnag?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingSnag;
    try {
      const result = await snagService?.deleteSnag(target?.id);
      if (result?.error) throw result?.error;
      setSnags((prev) => prev?.filter((s) => s?.id !== target?.id));
      showToast(`"${target?.title}" moved to Deleted`, 'success');
    } catch (err) {
      console.error('[SnagsListCreation] delete failed:', err);
      showToast(err?.message || 'Failed to delete snag', 'error');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingSnag(null);
    }
  };

  const handleRestore = async (snag) => {
    try {
      const result = await snagService?.restoreSnag(snag?.id);
      if (result?.error) throw result?.error;
      // Optimistic: drop from the deleted-tab list; the next switch
      // back to active view will pick it up via loadInitialData.
      setSnags((prev) => prev?.filter((s) => s?.id !== snag?.id));
      showToast(`"${snag?.title}" restored`, 'success');
    } catch (err) {
      console.error('[SnagsListCreation] restore failed:', err);
      showToast(err?.message || 'Failed to restore snag', 'error');
    }
  };

  const filteredSnags = useMemo(() => {
    return snags?.filter(snag => {
      if (filters?.status && snag?.status !== filters?.status) return false;
      if (filters?.priority && snag?.priority !== filters?.priority) return false;
      if (filters?.siteId && snag?.site_id !== filters?.siteId) return false;
      if (searchQuery) {
        const q = searchQuery?.toLowerCase();
        const matchTitle = snag?.title?.toLowerCase()?.includes(q);
        const matchDesc = snag?.description?.toLowerCase()?.includes(q);
        const matchLocation = snag?.location?.toLowerCase()?.includes(q);
        if (!matchTitle && !matchDesc && !matchLocation) return false;
      }
      return true;
    });
  }, [snags, filters, searchQuery]);

  const stats = useMemo(() => ({
    total: snags?.length,
    open: snags?.filter(s => s?.status === 'open')?.length,
    inProgress: snags?.filter(s => s?.status === 'in_progress')?.length,
    resolved: snags?.filter(s => s?.status === 'resolved')?.length
  }), [snags]);

  const resolvedCount = filteredSnags?.filter(s => s?.status === 'resolved')?.length || 0;
  const totalCount = filteredSnags?.length || 0;
  const resolutionRate = totalCount > 0 ? Math.round((resolvedCount / totalCount) * 100) : 0;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(true);
      const activeSite = filters?.siteId ? sites?.find(s => s?.id === filters?.siteId) : null;
      const result = await pdfExportService?.generateSnagReport({
        snags: filteredSnags,
        siteName: activeSite?.site_name || (filters?.siteId ? '' : 'All Sites'),
        companyName,
        filters,
      });
      if (result?.error) throw result.error;
      showToast(`PDF exported: ${result?.filename}`, 'success');
    } catch (err) {
      console.error('[SnagsListCreation] export PDF failed:', err);
      showToast('Failed to export PDF. Please try again.', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Snags - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <SubscriptionReadOnlyBanner className="mb-6" />
          <SiteReadOnlyBanner status={filterSiteStatus} siteId={siteIdParam} className="mb-6" />
          {/* Toast */}
          {toast && (
            <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}>
              <Icon name={toast?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={16} />
              {toast?.message}
            </div>
          )}

          <div className="mb-6 flex items-center gap-4">
            <Button variant="outline" size="sm" iconName="ArrowLeft" onClick={() => navigate(-1)}>{t('back')}</Button>
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {viewDeleted ? 'Deleted snags' : t('snags')}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {viewDeleted
                  ? 'Snags removed from active views. Restore to bring them back.'
                  : t('snagsSubtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* NSherjFh PR 4: admin-only View Deleted toggle. Same shape
                  as the toggles in document-management (PR 2) and
                  rams-management (PR 3). */}
              {isAdmin && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  size="sm"
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  onClick={() => { setActiveTab('list'); setViewDeleted((v) => !v); }}
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
              {!viewDeleted && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName={exportLoading ? 'Loader2' : 'Download'}
                  onClick={handleExportPDF}
                  disabled={exportLoading}
                >
                  {exportLoading ? t('exporting') : t('exportPDF')}
                </Button>
              )}
              {!viewDeleted && canCreate && (
                <Button
                  variant="primary"
                  size="default"
                  iconName="Plus"
                  className="min-h-[44px]"
                  onClick={() => setActiveTab('create')}
                  disabled={subscriptionReadOnly || filterSiteReadOnly}
                >
                  {t('reportSnag')}
                </Button>
              )}
            </div>
          </div>

          {/* Stats row — active view only; meaningless when looking at
              soft-deleted snags. */}
          {!viewDeleted && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {[
                { label: t('total'), value: stats?.total, icon: 'ClipboardList', color: 'text-foreground' },
                { label: t('open'), value: stats?.open, icon: 'AlertCircle', color: 'text-red-600' },
                { label: t('inProgress'), value: stats?.inProgress, icon: 'Clock', color: 'text-amber-600' },
                { label: t('resolved'), value: stats?.resolved, icon: 'CheckCircle2', color: 'text-green-600' }
              ]?.map(stat => (
                <div key={stat?.label} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-muted rounded-lg flex-shrink-0">
                    <Icon name={stat?.icon} size={20} className={stat?.color} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                    <p className="text-xs text-muted-foreground">{stat?.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* NSherjFh PR 4: active view = tabs (list / create); deleted
              view = simple list with Restore. Hide the tabs card on the
              deleted tab. */}
          {!viewDeleted && (
          <div className="bg-card border border-border rounded-lg shadow-elevation-2">
            {/* Tabs */}
            <div className="border-b border-border flex">
              <button
                onClick={() => setActiveTab('list')}
                className={`flex items-center justify-center gap-2 flex-1 sm:flex-none px-4 sm:px-6 py-4 min-h-[52px] font-medium text-sm transition-colors ${
                  activeTab === 'list' ? 'text-accent border-b-2 border-accent' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="List" size={16} />
                {t('allSnags')}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'list' ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
                }`}>{filteredSnags?.length}</span>
              </button>
              {canCreate && (
                <button
                  onClick={() => setActiveTab('create')}
                  className={`flex items-center justify-center gap-2 flex-1 sm:flex-none px-4 sm:px-6 py-4 min-h-[52px] font-medium text-sm transition-colors ${
                    activeTab === 'create' ? 'text-accent border-b-2 border-accent' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Plus" size={16} />
                  {t('reportSnag')}
                </button>
              )}
            </div>

            <div className="p-4 md:p-6">
              {activeTab === 'list' && (
                <div className="space-y-4">
                  {totalCount > 0 && (
                    <div className="bg-card rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">{t('snagResolution')}</span>
                        <span className="text-xs text-muted-foreground">{resolvedCount} of {totalCount} {t('resolved')?.toLowerCase()}</span>
                      </div>
                      <ProgressBar 
                        label={t('resolutionProgress')} 
                        value={resolutionRate} 
                        height="h-2.5" 
                        showValue={false} 
                        color="emerald" 
                      />
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />{t('open')}: {filteredSnags?.filter(s => s?.status === 'open')?.length || 0}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />{t('inProgress')}: {filteredSnags?.filter(s => s?.status === 'in_progress')?.length || 0}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />{t('resolved')}: {resolvedCount}</span>
                      </div>
                    </div>
                  )}
                  <SnagTable
                    snags={filteredSnags}
                    loading={loading}
                    onStatusChange={handleStatusChange}
                    onDelete={isAdmin ? handleDeleteClick : null}
                  />
                </div>
              )}
              {activeTab === 'create' && canCreate && (
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-4">{t('reportNewSnag')}</h2>
                  <SnagCreateForm
                    sites={sites}
                    companyId={companyId}
                    onSuccess={handleSnagCreated}
                    preselectedSiteId={siteIdParam || ''}
                    requirePhotoEvidenceSnag={requirePhotoEvidenceSnag}
                  />
                </div>
              )}
            </div>
          </div>
          )}

          {/* Deleted view: simple SnagTable with Restore. snag_comments
              on these rows remain in the DB (historical thread) and
              reappear automatically when the parent is restored. */}
          {viewDeleted && (
            <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
              <SnagTable
                snags={filteredSnags}
                loading={loading}
                onStatusChange={handleStatusChange}
                onRestore={isAdmin ? handleRestore : null}
              />
            </div>
          )}
        </main>
        <SnagDeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setDeletingSnag(null); }}
          onConfirm={handleConfirmDelete}
          snagTitle={deletingSnag?.title || ''}
        />
      </div>
    </>
  );
};

export default SnagsList;
